/** @typedef { { [key: string]: unknown }} IndexedObject */

/**
 * Determine if "obj1" and "obj2" are equivalent.
 * Primitive types are strictly compared,
 * while Array and Object types are deeply compared.
 *
 * @param { unknown } obj1
 * @param { unknown } obj2
 * @returns { boolean }
 */
export function isEquivalent(obj1, obj2) {
  if (obj1 === obj2) {
    return true;
  }

  const type1 = getType(obj1);
  const type2 = getType(obj2);

  if (type1 !== type2) {
    return false;
  } else if (type1 === 'array') {
    return isEquivalentArray(/** @type { Array<unknown> } */ (obj1), /** @type { Array<unknown> } */ (obj2));
  } else if (type1 === 'object') {
    return isEquivalentObject(/** @type { IndexedObject } */ (obj1), /** @type { IndexedObject } */ (obj2));
  } else if (type1 === 'date') {
    return /** @type { Date } */ (obj1).getTime() === /** @type { Date } */ (obj2).getTime();
  } else if (type1 === 'error') {
    const sameMessage = /** @type { Error } */ (obj1).message === /** @type { Error } */ (obj2).message;
    return sameMessage
      ? isEquivalentObject(/** @type { IndexedObject } */ (obj1), /** @type { IndexedObject } */ (obj2))
      : false;
  } else if (type1 === 'nan' && type2 === 'nan') {
    return true;
  }

  return obj1 === obj2;
}

/**
 * Determine if arrays "arr1" and "arr2" are equivalent.
 * Arrays are considered equivalent if they have the same length and equivalent items.
 *
 * @param { Array<unknown> } arr1
 * @param { Array<unknown> } arr2
 * @returns { boolean }
 */
function isEquivalentArray(arr1, arr2) {
  const n1 = arr1.length;
  const n2 = arr2.length;

  if (n1 !== n2) {
    return false;
  }

  for (let i = 0; i < n1; i++) {
    if (!isEquivalent(arr1[i], arr2[i])) {
      return false;
    }
  }

  return true;
}

/**
 * Determine if objects "obj1" and "obj2" are equivalent.
 * Objects are considered equivalent if they have the same number of properties with equivalent values.
 *
 * @param { IndexedObject } obj1
 * @param { IndexedObject } obj2
 * @returns { boolean }
 */
function isEquivalentObject(obj1, obj2) {
  const keys1 = getKeys(obj1);
  const keys2 = getKeys(obj2);

  if (keys1.length != keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = obj1[key];
    const val2 = obj2[key];

    if (!isEquivalent(val1, val2)) {
      return false;
    }
  }

  return true;
}

/**
 * Get type of "obj"
 *
 * @param { unknown } obj
 * @returns { string }
 */
function getType(obj) {
  /** @type { string } */
  let type = typeof obj;

  if (type === 'object') {
    if (obj instanceof Date) {
      type = 'date';
    } else if (obj instanceof Error) {
      type = 'error';
    } else if (Array.isArray(obj)) {
      type = 'array';
    } else if (obj === null) {
      type = 'null';
    }
  } else if (type === 'number' && isNaN(/** @type { number } */ (obj))) {
    type = 'nan';
  }

  return type;
}

/**
 * Retrieve keys of "obj"
 *
 * @param { IndexedObject } obj
 * @returns { Array<string> }
 */
function getKeys(obj) {
  const keys = [];

  for (const key in obj) {
    if (typeof obj[key] !== 'function') {
      keys.push(key);
    }
  }

  return keys;
}
